/**
 * SWOT Analysis Service
 * Handles API calls for SWOT (Strengths, Weaknesses, Opportunities, Threats) analysis
 */

import { apiClient } from './api';

export interface SWOTItem {
  id?: number;
  kata_board: number;
  swot_type: 'strength' | 'weakness' | 'opportunity' | 'threat';
  aspect: string;
  description: string;
  responsible?: string;
  date?: string;
  effectiveness?: string;
  // For threats - risk assessment
  probability?: number;
  impact?: number;
  risk_value?: number;
  risk_level?: 'low' | 'medium' | 'high';
  risk_level_display?: string;
  cause_analysis?: string;
  actions?: string;
  // Metadata
  order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  created_by_info?: {
    id: number;
    username: string;
    full_name: string;
  };
  is_threat_with_risk?: boolean;
}

export interface SWOTItemCreate {
  kata_board: number;
  swot_type: 'strength' | 'weakness' | 'opportunity' | 'threat';
  aspect: string;
  description: string;
  responsible?: string;
  date?: string;
  effectiveness?: string;
  probability?: number;
  impact?: number;
  cause_analysis?: string;
  actions?: string;
  order?: number;
}

export interface RiskMatrixData {
  threats: SWOTItem[];
  risk_statistics: {
    total_threats: number;
    high_risk: number;
    medium_risk: number;
    low_risk: number;
  };
}

export class SWOTService {
  private static baseUrl = '/swot-items';

  /**
   * Get all SWOT items for a specific KATA board
   */
  static async getByKataBoard(kataBoardId: number, swotType?: string): Promise<SWOTItem[]> {
    const params = new URLSearchParams({
      kata_board_id: kataBoardId.toString()
    });
    
    if (swotType) {
      params.append('swot_type', swotType);
    }

    const response = await apiClient.get(`${this.baseUrl}/by_kata_board/?${params}`);
    return response.data;
  }

  /**
   * Get a specific SWOT item by ID
   */
  static async getById(id: number): Promise<SWOTItem> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/`);
    return response.data;
  }

  /**
   * Create a new SWOT item
   */
  static async create(swotItem: SWOTItemCreate): Promise<SWOTItem> {
    const response = await apiClient.post(`${this.baseUrl}/`, swotItem);
    return response.data;
  }

  /**
   * Update an existing SWOT item
   */
  static async update(id: number, swotItem: Partial<SWOTItemCreate>): Promise<SWOTItem> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/`, swotItem);
    return response.data;
  }

  /**
   * Delete a SWOT item
   */
  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}/`);
  }

  /**
   * Get risk matrix data for threats in a KATA board
   */
  static async getRiskMatrixData(kataBoardId: number): Promise<RiskMatrixData> {
    const params = new URLSearchParams({
      kata_board_id: kataBoardId.toString()
    });

    const response = await apiClient.get(`${this.baseUrl}/risk_matrix_data/?${params}`);
    return response.data;
  }

  /**
   * Update risk assessment for a threat
   */
  static async updateRiskAssessment(
    id: number, 
    riskData: { probability?: number; impact?: number }
  ): Promise<SWOTItem> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/update_risk_assessment/`, riskData);
    return response.data;
  }

  /**
   * Create multiple SWOT items at once
   */
  static async bulkCreate(swotItems: SWOTItemCreate[]): Promise<{
    created_items: SWOTItem[];
    created_count: number;
    errors?: any[];
  }> {
    const response = await apiClient.post(`${this.baseUrl}/bulk_create/`, swotItems);
    return response.data;
  }

  /**
   * Get SWOT items filtered by type
   */
  static async getByType(
    kataBoardId: number, 
    swotType: 'strength' | 'weakness' | 'opportunity' | 'threat'
  ): Promise<SWOTItem[]> {
    return this.getByKataBoard(kataBoardId, swotType);
  }

  /**
   * Get all strengths for a KATA board
   */
  static async getStrengths(kataBoardId: number): Promise<SWOTItem[]> {
    return this.getByType(kataBoardId, 'strength');
  }

  /**
   * Get all weaknesses for a KATA board
   */
  static async getWeaknesses(kataBoardId: number): Promise<SWOTItem[]> {
    return this.getByType(kataBoardId, 'weakness');
  }

  /**
   * Get all opportunities for a KATA board
   */
  static async getOpportunities(kataBoardId: number): Promise<SWOTItem[]> {
    return this.getByType(kataBoardId, 'opportunity');
  }

  /**
   * Get all threats for a KATA board
   */
  static async getThreats(kataBoardId: number): Promise<SWOTItem[]> {
    return this.getByType(kataBoardId, 'threat');
  }

  /**
   * Calculate risk level based on probability and impact
   */
  static calculateRiskLevel(probability: number, impact: number): {
    level: 'low' | 'medium' | 'high';
    value: number;
    display: string;
  } {
    const riskValue = probability * impact;
    
    if (riskValue <= 6) {
      return { level: 'low', value: riskValue, display: 'Faible' };
    } else if (riskValue <= 15) {
      return { level: 'medium', value: riskValue, display: 'Moyen' };
    } else {
      return { level: 'high', value: riskValue, display: 'Élevé' };
    }
  }

  /**
   * Get risk color class for UI styling
   */
  static getRiskColorClass(riskLevel: 'low' | 'medium' | 'high'): string {
    switch (riskLevel) {
      case 'low': return 'bg-green-200 text-green-800';
      case 'medium': return 'bg-yellow-200 text-yellow-800';
      case 'high': return 'bg-red-200 text-red-800';
      default: return 'bg-gray-200 text-gray-800';
    }
  }

  /**
   * Validate SWOT item data before submission
   */
  static validateSWOTItem(swotItem: SWOTItemCreate): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Required fields
    if (!swotItem.aspect.trim()) {
      errors.push('Aspect is required');
    }

    if (!swotItem.swot_type) {
      errors.push('SWOT type is required');
    }

    // Threat-specific validation
    if (swotItem.swot_type === 'threat') {
      if (swotItem.probability !== undefined) {
        if (swotItem.probability < 1 || swotItem.probability > 5) {
          errors.push('Probability must be between 1 and 5 for threats');
        }
      }
      
      if (swotItem.impact !== undefined) {
        if (swotItem.impact < 1 || swotItem.impact > 5) {
          errors.push('Impact must be between 1 and 5 for threats');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default SWOTService;


